export type PlanType = 'free' | 'basic' | 'pro';

export interface PlanConfig {
  id: number;
  name: PlanType;
  displayName: string;
  storageBytes: number;
  storageGB: number;
  priceEur: number;
}

// Catalog prices, mirrored from api/stripe.mjs `PLANS[].price`. Used for
// upgrade-difference display in the FE — backend is the source of truth
// for what the customer is actually charged, this is just for surfacing
// "Upgrade for €150" before the redirect.
export const PLAN_CONFIG: Record<PlanType, PlanConfig> = {
  free: {
    id: 1,
    name: 'free',
    displayName: 'Blossom',
    storageBytes: 2 * 1024 * 1024 * 1024, // 2 GB
    storageGB: 2,
    priceEur: 0,
  },
  basic: {
    id: 2,
    name: 'basic',
    displayName: 'Bouquet',
    storageBytes: 50 * 1024 * 1024 * 1024, // 50 GB
    storageGB: 50,
    priceEur: 99,
  },
  pro: {
    id: 3,
    name: 'pro',
    displayName: 'Heirloom',
    storageBytes: 200 * 1024 * 1024 * 1024, // 200 GB (advertised "Unlimited, fair use")
    storageGB: 200,
    priceEur: 249,
  },
};

export const PLAN_HIERARCHY: PlanType[] = ['free', 'basic', 'pro'];

/**
 * Get plan configuration by plan type
 */
export const getPlanConfig = (planType: PlanType): PlanConfig => {
  return PLAN_CONFIG[planType];
};

/**
 * Check if targetPlan is an upgrade from currentPlan
 */
export const isUpgrade = (currentPlan: PlanType, targetPlan: PlanType): boolean => {
  const currentIndex = PLAN_HIERARCHY.indexOf(currentPlan);
  const targetIndex = PLAN_HIERARCHY.indexOf(targetPlan);
  return targetIndex > currentIndex;
};

/**
 * Check if targetPlan is a downgrade from currentPlan
 */
export const isDowngrade = (currentPlan: PlanType, targetPlan: PlanType): boolean => {
  const currentIndex = PLAN_HIERARCHY.indexOf(currentPlan);
  const targetIndex = PLAN_HIERARCHY.indexOf(targetPlan);
  return targetIndex < currentIndex;
};

/**
 * Effective price a couple will be charged when moving from `currentPlan`
 * to `targetPlan`. Mirrors `getUpgradePrice` in api/stripe.mjs — couples
 * pay the *difference* between tiers on an upgrade, not the full catalog
 * price. Returns the target's catalog price for first-time paid purchases
 * (Blossom → paid), and 0 for same-tier / downgrade calls (caller should
 * be gating those separately).
 */
export const getUpgradePriceEur = (currentPlan: PlanType, targetPlan: PlanType): number => {
  const diff = PLAN_CONFIG[targetPlan].priceEur - PLAN_CONFIG[currentPlan].priceEur;
  return diff > 0 ? diff : 0;
};

/**
 * Resolve a plan query-string value (from landing page or internal links)
 * to an internal PlanType. Accepts both display-name lowercase
 * (blossom/bouquet/heirloom) and internal names (free/basic/pro).
 * Returns 'free' for unknown / missing input.
 */
export const resolvePlanFromParam = (raw: string | null | undefined): PlanType => {
  switch ((raw || '').toLowerCase()) {
    case 'bouquet':
    case 'basic':
      return 'basic';
    case 'heirloom':
    case 'pro':
      return 'pro';
    case 'blossom':
    case 'free':
    default:
      return 'free';
  }
};
